import { useState } from 'react';
import {
  Alert, Box, Button, Chip, Collapse, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, FormControlLabel, IconButton, LinearProgress, Paper,
  Stack, Switch, Tooltip, Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ReplayIcon from '@mui/icons-material/Replay';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import TuneIcon from '@mui/icons-material/Tune';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutlined';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import type { ApiFallo, Descarga, ItemCola } from '../lib/api.js';
import { EditorPistas } from '../components/EditorPistas.js';
import { RegistroConversion } from '../components/RegistroConversion.js';
import { Reproductor } from '../components/Reproductor.js';
import { BotonCancelarConversion, useColaConversion } from '../components/ColaConversion.js';
import { DetalleAmule } from '../components/DetalleAmule.js';
import { GrupoTemporada } from '../components/GrupoTemporada.js';
import { agruparPorTemporadas } from '../lib/temporadas.js';
import { SelectorOrigen, contarOrigenes, origenInicial, pestanaDe } from '../components/SelectorOrigen.js';
import type { OrigenDescarga } from '../lib/api.js';
import {
  describirConversion, estimarEta, formatearDuracion, formatearTamano, formatearVelocidad,
  useEventos,
} from '../lib/eventos.js';
import { etiquetaEpisodio } from '@film/shared';
import { describirEstado } from '../lib/estados.js';

/** Qué queda en disco de una descarga terminada, y qué se lleva por delante borrarlo.
 *
 *  El fichero de la biblioteca NO entra en ninguno de los casos: eso se quita desde la
 *  biblioteca, y borrarlo por detrás dejaría a Plex con una ficha apuntando a un hueco. */
function queQuedaEnDisco(d: Descarga): { hay: boolean; texto: string; grave: boolean } {
  // Una fila de biblioteca (D-28): su «original» es el fichero de Plex y no se toca nunca
  // desde aquí. Lo único que se puede borrar es lo que espere en conversiones.
  if (d.origen === 'biblioteca') {
    return {
      hay: !!d.stagedPath,
      texto: d.stagedPath
        ? 'Se borra solo lo que espera en conversiones. El fichero de la biblioteca no se toca.'
        : 'No hay nada que borrar: el fichero de la biblioteca no se toca, y solo se quita del historial.',
      grave: false,
    };
  }
  if (d.originalReaped && !d.stagedPath) {
    return {
      hay: false,
      texto: 'No queda nada en disco de esta descarga: solo se quita del historial.',
      grave: false,
    };
  }
  if (d.status === 'imported') {
    return {
      hay: true,
      // Con `linked` la biblioteca es un tercer nombre del mismo inodo, así que borrar el
      // original solo quita un nombre. Con una conversión de verdad son ficheros distintos
      // y el de la biblioteca ni se mira. En los dos casos la película se queda.
      texto:
        'La copia de la biblioteca no se toca: se borra el original que seguía sembrando en ' +
        'aMule, y la película se sigue viendo igual.',
      grave: false,
    };
  }
  if (d.stagedKind === 'linked' && d.stagedPath) {
    return {
      hay: true,
      texto:
        'Lo que espera en conversiones y el original de aMule son el mismo dato con dos ' +
        'nombres: borrar los dos borra la película.',
      grave: true,
    };
  }
  return {
    hay: true,
    texto:
      'Se borran el original de aMule y lo que esperase en conversiones. Para recuperarla ' +
      'habría que volver a bajarla entera.',
    grave: true,
  };
}

function Detalle({ k, v }: { k: string; v: string }) {
  return (
    <Stack direction="row" spacing={2}>
      <Box sx={{ minWidth: 150, color: 'text.secondary' }}>{k}</Box>
      <Box sx={{ wordBreak: 'break-all' }}>{v}</Box>
    </Stack>
  );
}

/** Una fila de la lista, con TODO lo suyo dentro: sus mutaciones, sus diálogos y su
 *  desplegable.
 *
 *  No es una separación estética. Con las mutaciones en la página, `isPending` e `isError`
 *  eran de la lista entera: reconvertir una ponía «Reintentando…» en las cinco y apagaba
 *  sus botones, y el fallo de una pintaba la misma alerta debajo de todas. Cada fila tiene
 *  su propio estado por la misma razón por la que `FilaConversion` lo tiene: lo que le pasa
 *  a una película no le pasa a las demás. */
function FilaDescarga({ d, enCola }: { d: Descarga; enCola?: ItemCola }) {
  const qc = useQueryClient();
  // Solo el progreso de ESTE hash. Seleccionar el mapa entero repinta todas las filas cada
  // vez que llega un evento de cualquiera de ellas, y llegan cada segundo.
  const vivo = useEventos((s) => (d.ed2kHash ? s.progreso[d.ed2kHash] : undefined));
  // El de la preparación, por id de descarga. Va aparte del anterior porque son dos barras
  // distintas de la misma película: mientras se convierte, el original sigue sembrando en
  // aMule al 100 %, y pintarlos en la misma cifra era enseñar una y llamarla la otra.
  const progresoConversion = useEventos((s) => s.progresoConversion[d.id]);

  const [detalleAbierto, setDetalleAbierto] = useState(false);
  /** Si esta descarga tiene abierto el editor de pistas. */
  const [editando, setEditando] = useState(false);
  const [viendoRegistro, setViendoRegistro] = useState(false);
  /** Si esta descarga está abierta en el reproductor (D-26). */
  const [viendo, setViendo] = useState(false);
  /** Volver a bajar cuesta la descarga entera otra vez: se confirma. */
  const [aRedescargar, setARedescargar] = useState(false);
  const [aCancelar, setACancelar] = useState(false);
  const [borrarFicheros, setBorrarFicheros] = useState(false);
  /** Eliminar una terminada es otra cosa que cancelar una viva: no hay transferencia que
   *  parar y la fila desaparece del historial. Diálogo aparte, y casilla aparte, para que
   *  la de una no se quede marcada al abrir la otra. */
  const [aEliminar, setAEliminar] = useState(false);
  const [borrarAlEliminar, setBorrarAlEliminar] = useState(false);

  // Optimista: pausar y reanudar se pintan al instante y se revierten si el servidor
  // rechaza. Encolar NO es optimista — hasta que aMule acepta, no hay descarga.
  //
  // Se escribe sobre TODAS las cachés que empiezan por `descargas` porque la clave real
  // lleva el interruptor de «ver también las terminadas» (`['descargas', verTodas]`), y
  // apuntar solo a `['descargas']` no acertaba ninguna: el pintado optimista no se veía.
  // Y se revierte SOLO esta fila, no una instantánea de la lista, para no deshacer de paso
  // lo que hubiera cambiado en otra mientras tanto.
  const alternar = useMutation({
    mutationFn: () => (d.status === 'paused' ? api.reanudar(d.id) : api.pausar(d.id)),
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: ['descargas'] });
      const anterior = d.status;
      ponerEstado(qc, d.id, d.status === 'paused' ? 'downloading' : 'paused');
      return { anterior };
    },
    onError: (_e, _v, ctx) => {
      if (ctx) ponerEstado(qc, d.id, ctx.anterior);
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: ['descargas'] }),
  });

  /** Reintentar NO vuelve a descargar: el fichero ya está en disco y solo se repite la
   *  conversión, que deja la película otra vez en la carpeta de conversiones. Moverla a una
   *  biblioteca es un paso aparte, en Conversiones. */
  const reintentar = useMutation({
    mutationFn: () => api.reintentarPreparacion(d.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['descargas'] });
      void qc.invalidateQueries({ queryKey: ['conversiones'] });
    },
  });

  /** Vuelve a bajar el original sobre la MISMA descarga. Hace falta cuando el original ya
   *  no está: sin él no se puede reconvertir, porque el MP4 ya perdió lo que se descartó. */
  const redescargar = useMutation({
    mutationFn: () => api.volverADescargar(d.id),
    onSuccess: () => {
      setARedescargar(false);
      void qc.invalidateQueries({ queryKey: ['descargas'] });
      void qc.invalidateQueries({ queryKey: ['conversiones'] });
    },
  });

  const cancelar = useMutation({
    mutationFn: (borrar: boolean) => api.borrarDescarga(d.id, borrar),
    onSuccess: () => {
      setACancelar(false);
      setBorrarFicheros(false);
      void qc.invalidateQueries({ queryKey: ['descargas'] });
    },
  });

  /** Misma petición que cancelar —el servidor reparte según el estado— pero otra acción:
   *  aquí no hay descarga que parar y la fila se va del historial. Se invalidan también
   *  las conversiones: si lo que se borra estaba esperando allí, esa pantalla mentía. */
  const eliminar = useMutation({
    mutationFn: (borrar: boolean) => api.borrarDescarga(d.id, borrar),
    onSuccess: () => {
      setAEliminar(false);
      setBorrarAlEliminar(false);
      void qc.invalidateQueries({ queryKey: ['descargas'] });
      void qc.invalidateQueries({ queryKey: ['conversiones'] });
    },
  });

  /** Cerrar deja la casilla destructiva sin marcar: si se queda puesta, la siguiente
   *  película que se elimine se lleva sus ficheros sin que nadie lo haya pedido. */
  const cerrarEliminar = () => {
    setAEliminar(false);
    setBorrarAlEliminar(false);
    eliminar.reset();
  };

  const cerrarCancelar = () => {
    setACancelar(false);
    cancelar.reset();
  };

  const pct = vivo?.progressPct ?? d.progressPct;
  const hecho = vivo?.sizeDone ?? d.sizeDone;
  const velocidad = vivo?.speedBps ?? d.speedBps;
  const fuentes = vivo?.sourcesTotal ?? d.sourcesTotal;
  const activas = vivo?.sourcesXfer ?? d.sourcesXfer;
  const esperandoTurno = d.status === 'transcoding' && enCola?.estado === 'en_espera';
  const convirtiendo = d.status === 'transcoding' && !esperandoTurno;
  const estado = describirEstado(
    { ...d, sourcesTotal: fuentes, sourcesXfer: activas },
    enCola,
    // Esperando turno no hay fase que contar: el progreso que quedara de un intento
    // anterior diría que está leyendo subtítulos una película que ni ha empezado.
    esperandoTurno ? undefined : progresoConversion,
  );
  const fase = describirConversion(progresoConversion);
  // Al ritmo de este instante, que en eD2k cambia cada pocos segundos: de ahí el `~`
  // y el aviso. Sin fuentes no se estima nada en vez de fingir una cifra.
  const eta = d.status === 'downloading' ? estimarEta((d.sizeBytes ?? 0) - hecho, velocidad) : null;
  // `hashing` entra: aMule sigue teniéndola y trabajando en ella, así que el detalle vivo
  // tiene algo que contar y merece refrescarse.
  const enCurso =
    d.status === 'downloading' || d.status === 'paused' || d.status === 'queued' ||
    d.status === 'hashing';
  // Pausar, en cambio, no: en `hashing` ya no queda nada que bajar y aMule está ocupado
  // hasheando el fichero. Ofrecer el botón sería ofrecer algo que no hace nada.
  const pausable = enCurso && d.status !== 'hashing';
  // `completed` sin nada esperando en conversiones es donde queda una descarga a la
  // que se le eliminó la conversión: reconvertir es justo lo que falta ofrecerle. Con
  // el original retirado no hay de dónde, y entonces lo que toca es volver a bajarla.
  const reconvertibleDesdeCero =
    d.status === 'completed' && d.stagedPath === null && !d.originalReaped;
  const reintentable =
    d.status === 'transcode_failed' || d.status === 'import_failed' || reconvertibleDesdeCero;
  // La condición es tener fichero esperando, no el estado: un `import_failed` que
  // falló ANTES de convertir no aparece en Conversiones, y mandar allí sería mandar
  // a una pantalla vacía.
  const enConversiones = d.stagedPath !== null;
  // Editar las pistas exige que el ORIGINAL siga en la carpeta de aMule: es de donde
  // se lee para reconvertir. Retirado por la recogida, ya no hay nada que reconvertir.
  //
  // `imported` entra también, y es lo que hace falta cuando la conversión salió mal y uno
  // no se da cuenta hasta verla en Plex. Reconvertir la devuelve a Conversiones y de ahí
  // se mueve otra vez a la biblioteca, sustituyendo a la copia de antes; el original nunca
  // se vuelve a descargar. Lo único que lo impide es que la recogida ya se lo llevara, que
  // es exactamente lo que comprueba `originalReaped`.
  const editable =
    !d.originalReaped &&
    (d.status === 'transcode_failed' || d.status === 'ready_to_place' ||
      d.status === 'import_failed' || d.status === 'imported' || reconvertibleDesdeCero);
  // Y al revés: sin original no hay nada que reconvertir, así que lo único que
  // devuelve la partida es volver a bajarlo. `imported` no entra — ya está colocada, y
  // bajarla otra vez solo dejaría dos copias.
  const sinOriginal =
    d.originalReaped &&
    (d.status === 'ready_to_place' || d.status === 'import_failed' || d.status === 'transcode_failed');
  const rebajable =
    sinOriginal || d.status === 'failed' || d.status === 'cancelled';
  // Terminada y quieta: no hay transferencia que cancelar ni fichero moviéndose
  // debajo. `transcoding` e `importing` quedan fuera —ffmpeg escribiendo o una
  // película a medio mover no se les quita la fila de debajo—, y el servidor lo
  // vuelve a comprobar por su cuenta.
  const eliminable = !enCurso && d.status !== 'transcoding' && d.status !== 'importing';
  // El registro se ofrece desde que la preparación puede haber empezado. Es la condición
  // de una descarga que YA pasó de descargarse, no la de tener fichero convertido: cuando
  // ffmpeg falla no hay fichero ninguno, y es justo entonces cuando hay que poder leerlo.
  const hayRegistro =
    !enCurso && d.status !== 'failed' && d.status !== 'cancelled';
  // Ver la película en el navegador (D-26): desde que hay un fichero entero en disco. Cuál
  // —el original, la conversión, la copia de la biblioteca— lo dice el propio reproductor
  // al abrirse, mirando el disco; si no queda ninguno, lo dice también.
  const visible = !enCurso && d.status !== 'failed' && d.status !== 'cancelled';
  const queda = queQuedaEnDisco(d);

  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ alignItems: { sm: 'center' } }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body1" sx={{ fontWeight: 600 }} noWrap>
            {d.movieTitle ?? d.fileName}
            {d.movieYear && <Typography component="span" color="text.secondary"> ({d.movieYear})</Typography>}
            {/* Un episodio se distingue de sus hermanos por esto y no por el título, que es
                el mismo en los veintidós (D-27). */}
            {etiquetaEpisodio(d.season, d.episode, d.episodeEnd) && (
              <Typography component="span" color="text.secondary"> · {etiquetaEpisodio(d.season, d.episode, d.episodeEnd)}</Typography>
            )}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
            {[d.origen === 'biblioteca' ? 'desde la biblioteca' : d.quality, d.movieTitle ? d.fileName : null]
              .filter(Boolean)
              .join(' · ')}
          </Typography>
        </Box>

        {/* Con `import_failed` esta fila lleva estado, «Colocar», pistas,
            «Reconvertir» y el desplegable: a 375 px se sale de la tarjeta si no puede
            partirse en dos líneas. */}
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}
        >
          <Tooltip title={estado.nota ?? ''}>
            <Chip size="small" color={estado.color} label={estado.texto} />
          </Tooltip>
          {pausable && (
            <IconButton size="small" disabled={alternar.isPending} onClick={() => alternar.mutate()}>
              {d.status === 'paused' ? <PlayArrowIcon fontSize="small" /> : <PauseIcon fontSize="small" />}
            </IconButton>
          )}
          {/* Convirtiéndose o esperando turno: se puede parar. La condición es estar en la
              cola y no el estado — `transcoding` sin cola es una conversión de antes de
              reiniciar la API, que ya no tiene ningún proceso detrás que matar. */}
          {enCola && <BotonCancelarConversion item={enCola} />}
          {visible && (
            <Tooltip title="Ver la película en el navegador antes de decidir: el original, la conversión o la copia de la biblioteca, con sus pistas de audio y subtítulos">
              <IconButton size="small" onClick={() => setViendo(true)}>
                <PlayCircleOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {enConversiones && (
            <Button size="small" variant="contained" component={Link} to="/conversiones">
              Colocar
            </Button>
          )}
          {editable && (
            <Tooltip
              title={
                d.status === 'imported'
                  ? 'Corregir las pistas y volver a convertir. El original de aMule sigue ahí: no se descarga nada, y la copia de la biblioteca se sustituye al moverla otra vez.'
                  : 'Ver y corregir las pistas antes de volver a convertir'
              }
            >
              <IconButton size="small" onClick={() => setEditando(true)}>
                <TuneIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {rebajable && (
            <Tooltip
              title={
                sinOriginal
                  ? 'El original ya no está: sin él no se puede reconvertir. Vuelve a bajarlo.'
                  : 'Vuelve a bajar el fichero sobre esta misma descarga'
              }
            >
              <IconButton size="small" onClick={() => setARedescargar(true)}>
                <CloudDownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {reintentable && (
            <Tooltip title="Vuelve a convertir y la deja en conversiones. NO descarga nada otra vez.">
              <span>
                <Button
                  size="small"
                  variant={enConversiones ? 'outlined' : 'contained'}
                  startIcon={<ReplayIcon />}
                  disabled={reintentar.isPending}
                  onClick={() => reintentar.mutate()}
                >
                  {reintentar.isPending ? 'Reintentando…' : 'Reconvertir'}
                </Button>
              </span>
            </Tooltip>
          )}
          {/* La misma papelera para las dos cosas, porque para quien mira es la
              misma intención: quitar esto de aquí. Lo que cambia es el diálogo, y
              cambia de verdad — una avisa de que pierdes lo descargado y la otra de
              que la fila desaparece del historial. */}
          {enCurso && (
            <Tooltip title="Cancelar la descarga">
              <IconButton size="small" onClick={() => setACancelar(true)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {eliminable && (
            <Tooltip title="Eliminar esta descarga del historial">
              <IconButton size="small" onClick={() => setAEliminar(true)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {hayRegistro && (
            <Tooltip title="Ver el registro de la conversión: qué se decidió y qué dijo ffmpeg">
              <IconButton size="small" onClick={() => setViendoRegistro(true)}>
                <ArticleOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Ver el detalle">
            <IconButton size="small" onClick={() => setDetalleAbierto(!detalleAbierto)}>
              <ExpandMoreIcon
                fontSize="small"
                sx={{ transform: detalleAbierto ? 'rotate(180deg)' : 'none', transition: '0.2s' }}
              />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <Box sx={{ mt: 1.5 }}>
        {/* Convirtiendo, la barra es la de la preparación y no la de la descarga: esa
            lleva al 100 % desde que terminó de bajarse y ahí se quedaría, quieta, dando la
            sensación de que nada avanza. Y lo que pinta no es siempre ffmpeg: analizar y
            leer los subtítulos por OCR van antes, y el OCR trae su propio porcentaje —las
            imágenes ya leídas (D-19)— mientras que analizar no, así que ahí la barra se
            queda indefinida y quien cuenta lo que pasa es el pie. Esperando turno no hay
            nada que medir todavía. */}
        {convirtiendo ? (
          <>
            <LinearProgress
              variant={fase.porcentaje == null ? 'indeterminate' : 'determinate'}
              value={fase.porcentaje ?? 0}
              color="info"
            />
            {/* El pie del OCR es largo —cuál de cuántos, cuántas imágenes, cuánto queda— y
                a 375 px no cabe en la misma línea que el tamaño: se deja partir en dos. */}
            <Stack
              direction="row"
              sx={{ justifyContent: 'space-between', mt: 0.5, flexWrap: 'wrap', columnGap: 1 }}
            >
              <Tooltip title={fase.nota}>
                <Typography variant="caption" color="text.secondary">
                  {fase.texto}
                </Typography>
              </Tooltip>
              <Typography variant="caption" color="text.secondary">
                Descargada entera: {formatearTamano(d.sizeBytes)}
              </Typography>
            </Stack>
          </>
        ) : (
          <>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, pct)}
              color={estado.color === 'error' ? 'error' : estado.color === 'warning' ? 'warning' : 'primary'}
            />
            <Stack direction="row" sx={{ justifyContent: 'space-between', mt: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                {pct.toFixed(1)}% · {formatearTamano(hecho)} de {formatearTamano(d.sizeBytes)}
              </Typography>
              {/* Una fila de biblioteca (D-28) nunca pasó por la red: velocidad y fuentes
                  serían ceros que no describen nada. */}
              {d.origen !== 'biblioteca' && (
              <Typography variant="caption" color="text.secondary">
                {formatearVelocidad(velocidad)} · {activas} de {fuentes} fuentes
                {eta !== null && (
                  <Tooltip title="Estimado a la velocidad de ahora mismo. En eD2k sube y baja mucho, así que el número se mueve.">
                    <Box component="span"> · faltan ~{formatearDuracion(eta)}</Box>
                  </Tooltip>
                )}
              </Typography>
              )}
            </Stack>
          </>
        )}
        {estado.nota && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {estado.nota}
          </Typography>
        )}

        {/* El motivo concreto del fallo, no solo que falló. */}
        {d.errorDetail && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            {d.errorDetail}
          </Alert>
        )}

        {/* Que las pistas no se puedan editar tiene una causa concreta y un arreglo
            concreto. Sin decirlo, el botón simplemente no está y no se sabe por qué. */}
        {sinOriginal && (
          <Alert severity="info" sx={{ mt: 1 }}>
            El original de aMule ya no está, así que esta conversión no se puede rehacer:
            el MP4 perdió las pistas que se descartaron. La conversión que hay sigue
            siendo válida y se puede colocar; para rehacerla, hay que volver a bajarlo.
          </Alert>
        )}

        {/* Pausar y reanudar fallan por esta película, no por la lista: el aviso va aquí
            y no en la cabecera, donde no se sabía a cuál se refería. */}
        {alternar.isError && (
          <Alert severity="error" sx={{ mt: 1 }} onClose={() => alternar.reset()}>
            No se pudo cambiar el estado: {(alternar.error as ApiFallo).message}
          </Alert>
        )}

        {reintentar.isError && (
          <Alert severity="error" sx={{ mt: 1 }} onClose={() => reintentar.reset()}>
            {(reintentar.error as ApiFallo).message}
          </Alert>
        )}
      </Box>

      <Collapse in={detalleAbierto}>
        <Box sx={{ mt: 1.5, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
          <Stack spacing={0.5} sx={{ fontFamily: 'monospace', fontSize: 12 }}>
            <Detalle k="Fichero" v={d.fileName} />
            {d.ed2kHash && <Detalle k="Hash ed2k" v={d.ed2kHash} />}
            <Detalle k="Estado" v={d.status} />
            <Detalle k="Encolada" v={new Date(d.createdAt).toLocaleString('es-ES')} />
            {d.completedAt && (
              <Detalle k="Descarga terminada" v={new Date(d.completedAt).toLocaleString('es-ES')} />
            )}
            {d.sourcePath && <Detalle k={d.origen === 'biblioteca' ? 'Fichero de la biblioteca' : 'Original en aMule'} v={d.sourcePath} />}
            {d.sustituyeRuta && <Detalle k="Sustituirá a" v={d.sustituyeRuta} />}
            {d.stagedPath && <Detalle k="Esperando en" v={d.stagedPath} />}
            {d.targetPath && <Detalle k="Colocada en" v={d.targetPath} />}
            {d.importMethod && (
              <Detalle
                k="Método"
                v={
                  d.importMethod === 'move'
                    ? 'movida desde conversiones (sin ocupar el doble)'
                    : d.importMethod === 'hardlink'
                      ? 'enlace duro (no ocupa el doble)'
                      : 'copia'
                }
              />
            )}
            {d.stagedKind && (
              <Detalle
                k="Conversión"
                v={
                  d.stagedKind === 'linked'
                    ? 'ninguna: es el fichero de aMule enlazado'
                    : d.stagedKind === 'copied'
                      ? 'ninguna: es una copia del fichero de aMule, y ocupa el doble'
                      : d.stagedKind === 'remux'
                        ? 'remux: solo cambió el contenedor'
                        : 'recodificada'
                }
              />
            )}
            {d.stagedKind && d.stagedKind !== 'linked' && d.origen !== 'biblioteca' && (
              <Detalle
                k="Original"
                v={d.originalReaped ? 'ya retirado' : 'sigue compartiéndose en aMule'}
              />
            )}
            {d.errorCode && <Detalle k="Código de error" v={d.errorCode} />}
          </Stack>

          {/* Lo que sabe aMule: partes, disponibilidad y relojes. Se pide solo con el
              detalle abierto —de ahí el `detalleAbierto &&`, y no basta el `Collapse`,
              que mantiene montado lo que hay dentro— porque el mapa de partes son
              cientos de entradas por fichero. */}
          {detalleAbierto && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                En la red eD2k
              </Typography>
              <DetalleAmule downloadId={d.id} viva={enCurso} estado={d.status} />
            </Box>
          )}
        </Box>
      </Collapse>

      {viendoRegistro && (
        <RegistroConversion
          downloadId={d.id}
          titulo={d.movieTitle ?? d.fileName}
          abierto={viendoRegistro}
          onCerrar={() => setViendoRegistro(false)}
        />
      )}

      {viendo && (
        <Reproductor
          downloadId={d.id}
          titulo={d.movieTitle ?? d.fileName}
          abierto={viendo}
          onCerrar={() => setViendo(false)}
        />
      )}

      {editando && (
        <EditorPistas
          downloadId={d.id}
          titulo={d.movieTitle ?? d.fileName}
          abierto
          yaColocada={d.status === 'imported'}
          onCerrar={() => setEditando(false)}
          onReconvertida={() => {
            void qc.invalidateQueries({ queryKey: ['descargas'] });
            void qc.invalidateQueries({ queryKey: ['conversiones'] });
          }}
        />
      )}

      {/* Bajar de nuevo cuesta la descarga entera y el tiempo que tarde la red eD2k: se
          confirma, y se dice qué NO se toca, que es lo que suele preocupar. */}
      <Dialog open={aRedescargar} onClose={() => setARedescargar(false)} fullWidth maxWidth="sm">
        <DialogTitle>Volver a descargar el original</DialogTitle>
        <DialogContent>
          <DialogContentText>
            «{d.movieTitle ?? d.fileName}» se vuelve a pedir a aMule sobre esta misma
            descarga, con el mismo enlace ed2k. Puede tardar lo que tardó la primera vez.
          </DialogContentText>
          <Alert severity="info" sx={{ mt: 2 }}>
            La conversión que ya espera en Conversiones <strong>no se toca</strong> hasta que
            la nueva termine y la sustituya: si te arrepientes, sigues pudiendo colocarla. Si
            habías editado las pistas, ese plan se conserva y se aplica a la nueva.
          </Alert>
          {redescargar.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {(redescargar.error as ApiFallo).message}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setARedescargar(false); redescargar.reset(); }}>Volver</Button>
          <Button
            variant="contained"
            disabled={redescargar.isPending}
            onClick={() => redescargar.mutate()}
          >
            {redescargar.isPending ? 'Encolando…' : 'Volver a descargar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Eliminar una terminada no pierde ninguna descarga en curso, pero sí es
          irreversible en otro sentido: la fila se va del historial. Y la casilla de
          ficheros no significa lo mismo según dónde acabara la película, así que se dice
          en cada caso en vez de con una frase que sirva para todos. */}
      <Dialog open={aEliminar} onClose={cerrarEliminar} fullWidth maxWidth="sm">
        <DialogTitle>Eliminar la descarga</DialogTitle>
        <DialogContent>
          <DialogContentText>
            «{d.movieTitle ?? d.fileName}» desaparece de esta lista y del historial. Ya no
            hay nada que cancelar: la descarga terminó hace rato.
          </DialogContentText>
          {queda.hay ? (
            <>
              <FormControlLabel
                sx={{ mt: 2 }}
                control={
                  <Switch
                    checked={borrarAlEliminar}
                    onChange={(e) => setBorrarAlEliminar(e.target.checked)}
                  />
                }
                label="Borrar también los ficheros que queden en disco"
              />
              <Alert severity={borrarAlEliminar && queda.grave ? 'warning' : 'info'} sx={{ mt: 1 }}>
                {queda.texto}
              </Alert>
            </>
          ) : (
            <Alert severity="info" sx={{ mt: 2 }}>
              {queda.texto}
            </Alert>
          )}
          {/* La fila es lo único que impide encolar dos veces el mismo enlace: sin
              ella, la película vuelve a aparecer como si nunca se hubiera bajado. Y
              es también lo único que la recogida mira, así que un fichero que se
              deja en disco ya no lo retirará nadie solo. */}
          <Alert severity="info" sx={{ mt: 1 }}>
            Sin la fila en el historial, esta película se puede volver a encolar desde
            su ficha como si fuera nueva.
            {queda.hay && !borrarAlEliminar && (
              <> Y lo que se quede en disco deja de estar a la vista de la recogida:
              solo mira descargas del historial, así que habría que borrarlo a mano.</>
            )}
          </Alert>
          {eliminar.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              No se pudo eliminar: {(eliminar.error as ApiFallo).message}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={cerrarEliminar}>Volver</Button>
          <Button
            color="error"
            disabled={eliminar.isPending}
            onClick={() => eliminar.mutate(borrarAlEliminar)}
          >
            {eliminar.isPending ? 'Eliminando…' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancelar al 90% sin avisar de que se pierden los datos parciales sería una
          sorpresa cara: se confirma, y borrar es una opción aparte. */}
      <Dialog open={aCancelar} onClose={cerrarCancelar}>
        <DialogTitle>Cancelar la descarga</DialogTitle>
        <DialogContent>
          <DialogContentText>
            «{d.movieTitle ?? d.fileName}» va por el {pct.toFixed(1)}%.
          </DialogContentText>
          <FormControlLabel
            sx={{ mt: 2 }}
            control={<Switch checked={borrarFicheros} onChange={(e) => setBorrarFicheros(e.target.checked)} />}
            label="Borrar también lo descargado hasta ahora"
          />
          {borrarFicheros && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              Se perderá {formatearTamano(hecho)} ya descargados.
            </Alert>
          )}
          {/* Sin esto, un fallo dejaba el diálogo quieto y sin explicación: parecía que
              el botón no hacía nada. */}
          {cancelar.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              No se pudo cancelar: {(cancelar.error as ApiFallo).message}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={cerrarCancelar}>Volver</Button>
          <Button
            color="error"
            disabled={cancelar.isPending}
            onClick={() => cancelar.mutate(borrarFicheros)}
          >
            {cancelar.isPending ? 'Cancelando…' : 'Cancelar la descarga'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

/** Cambia el estado de UNA descarga en todas las cachés de la lista, sea cual sea el
 *  interruptor de «ver también las terminadas» con el que se pidieran. */
function ponerEstado(
  qc: ReturnType<typeof useQueryClient>,
  id: string,
  status: Descarga['status'],
) {
  qc.setQueriesData<{ items: Descarga[] }>({ queryKey: ['descargas'] }, (v) =>
    v ? { ...v, items: v.items.map((x) => (x.id === id ? { ...x, status } : x)) } : v,
  );
}

export function Descargas() {
  const [verTodas, setVerTodas] = useState(false);
  // Descargas de HispaShare y conversiones desde la biblioteca (D-28), en pestañas: `null`
  // es «la que toque», que se decide con la primera lista que llega.
  const [origen, setOrigen] = useState<OrigenDescarga | null>(null);

  const { data, error } = useQuery({
    queryKey: ['descargas', verTodas],
    queryFn: () => api.descargas(verTodas),
    refetchInterval: 30_000,
  });

  // La cola de conversión es lo que separa «convirtiendo» de «esperando turno», y solo
  // existe en memoria de la API: se vuelve a pedir con cada evento.
  const { data: cola } = useColaConversion();
  const enCola = new Map((cola?.items ?? []).map((i) => [i.downloadId, i]));

  if (error) return <Alert severity="error">No se pudo leer la cola de descargas.</Alert>;
  const todas = data?.items ?? [];
  const cuentas = contarOrigenes(todas);
  const pestana = origen ?? origenInicial(cuentas);
  const items = todas.filter((d) => pestanaDe(d) === pestana);

  return (
    <Stack spacing={2}>
      {/* En columna a partir de 375 px: en una fila, el título y la etiqueta del
          interruptor se pisan y ninguno de los dos se lee (X-3). */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        sx={{ alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between' }}
      >
        <Typography variant="h5">Descargas</Typography>
        <FormControlLabel
          control={<Switch size="small" checked={verTodas} onChange={(e) => setVerTodas(e.target.checked)} />}
          label="Ver también las terminadas y canceladas"
        />
      </Stack>

      <SelectorOrigen valor={pestana} onCambio={setOrigen} cuentas={cuentas} />

      {items.length === 0 && (
        <Alert severity="info">
          {pestana === 'biblioteca'
            ? 'No hay nada pedido desde Biblioteca' +
              (verTodas ? '.' : ' en curso. Lo ya colocado sale con «Ver también las terminadas».')
            : verTodas
              ? 'No hay ninguna descarga todavía.'
              : 'No hay descargas en curso.'}
        </Alert>
      )}

      {/* Los episodios de una misma temporada, en una tarjeta (D-27): veintidós filas con el
          mismo título no dicen nada, y cada uno sigue teniendo la suya al desplegar. */}
      {agruparPorTemporadas(items).map((g) =>
        g.tipo === 'suelta' ? (
          <FilaDescarga key={g.d.id} d={g.d} enCola={enCola.get(g.d.id)} />
        ) : (
          <GrupoTemporada
            key={`${g.descargas[0]!.movieId}:${g.descargas[0]!.season ?? 'sin'}`}
            descargas={g.descargas}
            fila={(d) => <FilaDescarga d={d} enCola={enCola.get(d.id)} />}
          />
        ),
      )}
    </Stack>
  );
}
